// src/pages/Ads/AdsPolicy.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useFixedLayout } from "../../hooks/useFixedLayout";
import { HeaderIconL } from "../../assets/components/HeaderGroup";
import left from "../../assets/icons/Arrow/left.svg";
import { POLICY_COMPONENTS } from "./Components/policyMap";
import PolicyVersionSelectSheet from "../Ads/components/BottomSheet/PolicyVersionSelectSheet";
import down from "../../assets/icons/Arrow/down_tri.svg";

const AdsPolicy = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { headerRef } = useFixedLayout();
    const [versions, setVersions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isVersionSelectOpen, setIsVersionSelectOpen] = useState(false);


    // ─────────────────────────────────────────
    // 1) state 해석 (옛날/새 형식 둘 다 지원)
    //    - 예전: navigate("/ads/policy", { state: "terms" })
    //    - 현재: navigate("/ads/policy", { state: { type: "terms" } })
    // ─────────────────────────────────────────
    const rawState = location.state;

    let policyType = "terms"; // 기본값

    if (rawState) {
        if (typeof rawState === "string") {
            // 옛날 방식: state 자체가 "terms" / "privacy" / "permission"
            if (["terms", "privacy", "permission"].includes(rawState)) {
                policyType = rawState;
            }
        } else if (typeof rawState === "object" && rawState.type) {
            if (["terms", "privacy", "permission"].includes(rawState.type)) {
                policyType = rawState.type;
            }
        }
    }

    // ─────────────────────────────────────────
    // 2) API type 매핑
    //    DB ads_policy_version.type 값과 맞춰야 함
    // ─────────────────────────────────────────
    const apiTypeMap = {
        terms: "TOS",
        privacy: "PRIVACY",
        permission: "PERMISSION", // 접근 권한 안내
    };
    const apiType = apiTypeMap[policyType] ?? "TOS";

    const selectedLabel =
        selected
            ? (selected.is_active ? `[현행] ${selected.version_label}` : selected.version_label)
            : "";


    const handleHome = () => {
        navigate("/ads/main", { state: { tab: "mypage" } });
    };

    // 3) 버전 리스트 가져오기
    useEffect(() => {
        const fetchVersions = async () => {
            setLoading(true);
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_FASTAPI_ADS_URL}/policy/list`,
                    { params: { type: apiType } }
                );

                const list = res.data || [];
                setVersions(list);
                console.log(res.data)

                // 기본 선택: is_active == true 인 것 또는 첫 번째
                const active = list.find((v) => v.is_active) || list[0];
                if (active) {
                    setSelected(active);
                } else {
                    setSelected(null);
                }
            } catch (e) {
                console.error("policy list error", e);
                alert("정책 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
            } finally {
                setLoading(false);
            }
        };

        fetchVersions();
    }, [apiType]);

    // 4) 헤더 타이틀
    const pageTitleMap = {
        terms: "서비스 이용약관",
        privacy: "개인정보처리방침",
        permission: "접근 권한",
    };
    const pageTitle = pageTitleMap[policyType] ?? "정책 안내";

    // 5) 컴포넌트 매핑 (component_key → JSX 컴포넌트)
    const SelectedComponent =
        selected && selected.component_key
            ? POLICY_COMPONENTS[selected.component_key]
            : null;

    return (
        <div className="max-w-[720px] mx-auto pt-4 pb-8">
            {/* 상단 헤더 */}
            <div className="flex items-center min-w-0 pb-8">
                <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
                    <HeaderIconL
                        className=""
                        title={pageTitle}
                        icon={left}
                        onIconClick={handleHome}
                    />
                </div>
            </div>

            {/* 버전 선택 셀렉트 */}
            {/* 버전 선택 셀렉트 (UI는 그대로, 클릭 시 바텀시트 오픈) */}
            {versions.length > 1 && (
                <div className="mt-12 mb-4">
                    <button
                        type="button"
                        className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm flex items-center justify-between"
                        onClick={() => {
                            if (!loading) setIsVersionSelectOpen(true);
                        }}
                    >
                        <span
                            className={
                                selectedLabel
                                    ? "text-b1 text-text-default"
                                    : "text-b1 text-text-subtler"
                            }
                        >
                            {selectedLabel || "버전을 선택해 주세요"}
                        </span>

                        {/* 셀렉트 박스 오른쪽 화살표처럼 보이게 */}
                        <img src={down} alt="열기" className="w-[16px] h-[16px]" />
                    </button>
                </div>
            )}



            {/* 내용 영역 */}
            <div className="p-4  overflow-y-auto">
                {loading ? (
                    <p className="text-sm text-gray-400">불러오는 중…</p>
                ) : SelectedComponent ? (
                    <SelectedComponent />
                ) : (
                    <p className="text-sm text-gray-400">표시할 정책이 없습니다.</p>
                )}
            </div>

            <PolicyVersionSelectSheet
                isOpen={isVersionSelectOpen}
                onClose={() => setIsVersionSelectOpen(false)}
                versions={versions}
                value={selected}
                onConfirm={(row) => {
                    setSelected(row);
                }}
            />
        </div>
    );
};

export default AdsPolicy;
